import { Grid, Typography } from '@mui/material';
import Router, { useRouter } from 'next/router';
import { getSession, signIn, signOut } from 'next-auth/react';
import React, { useEffect, useReducer, useState } from 'react';
import { EngagementEvents, identify, logKariusEvent } from '../src/Analytics';
import { logError } from '../src/Analytics/AnalyticsClient';
import { checkIfTokenObjectValid } from '../src/api/ApiUtils';
import KariusLogoWidget from '../src/assets/images/svg/KariusLogoWidget';
import { AppUser, UserRole } from '../src/auth/authTypes';
import getRoleBasedRedirectUrl from '../src/auth/getRoleBasedRedirect';
import LoginFooter from '../src/Footer/LoginFooter';
import LoginForm from '../src/Forms/Login/LoginForm';
import { LoginHelpSection } from '../src/Login/HelpAccordion';
import { MobileMarketing } from '../src/Login/MobileMarketing';
import { AccountBlocked } from '../src/Modal/AccountBlocked';
import { clearUserSessionStorage } from '../src/sessionStorage/utils';
import Colors from '../src/theme/colors';

function Login() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [accountBlockedModalOpen, setAccountBlockedModalOpen] = useState(false);
  const [modalFromUrlShowed, setModalFromUrlShowed] = useState(false);
  const [attempts, incrementAttempts] = useReducer((value) => value + 1, 1); // incorrect password sign in time

  const isRedirectFromBlockedExpiredPage = () => {
    return (
      router.query['redirectFromBlockEmail'] === 'true' &&
      router.query['message'] === 'Access expired.' &&
      router.query['success'] === 'false'
    );
  };

  if (isRedirectFromBlockedExpiredPage() && !modalFromUrlShowed) {
    setAccountBlockedModalOpen(true);
    setModalFromUrlShowed(true);
    logKariusEvent(EngagementEvents.account_locked_button_expired_shown);
  }

  useEffect(() => {
    async function checkUserRole() {
      const session = await getSession();
      const sessionUser: any = session?.user;
      if (sessionUser) {
        if (!!sessionUser?.access_token && !checkIfTokenObjectValid(sessionUser)) {
          await signOut(); // token exist but expired, need to sign out
        } else if (hasValidUserRole(sessionUser) && checkIfTokenObjectValid(sessionUser)) {
          await redirectByUserRole(sessionUser.role); // user has valid token, could directly sign in
        }
      }
    }

    checkUserRole();
  }, []);

  const onSubmitLogin = async (values: any, showLoginError: (message: any) => void) => {
    logKariusEvent(EngagementEvents.sign_in_tapped);
    setLoading(true);
    await signIn('credentials', {
      redirect: false,
      email: values.email,
      password: values.password,
    });

    const session = await getSession();
    const sessionUser: any = session?.user;
    // if errors sent from backend
    if (sessionUser && sessionUser?.error) {
      setLoading(false);
      logError('error submitting login. Result:', sessionUser);
      console.error('error submitting login. Result:', sessionUser);
      if (!sessionUser.error_description || sessionUser.error_description === 'Wrong email or password.') {
        if (attempts === 5) {
          showLoginError('One more unsuccessful sign in attempt and your account will become locked.');
        } else if (attempts === 6) {
          // after 6 failed attempts account is auto locked
          showLoginError('Incorrect username or password.');
          setAccountBlockedModalOpen(true);
          logKariusEvent(EngagementEvents.account_locked_shown, {
            email_or_username: values.email,
          });
        } else {
          showLoginError('Incorrect username or password');
        }
        incrementAttempts();
      } else if (sessionUser.error === 'too_many_attempts') {
        setAccountBlockedModalOpen(true);
        logKariusEvent(EngagementEvents.account_locked_shown, {
          email_or_username: values.email,
        });
        showLoginError(
          'Your account is locked. Please check your email for a method to unlock your account, or you can reset your password.',
        );
      } else {
        showLoginError(sessionUser.error_description);
      }
      return;
    }

    if (!sessionUser) {
      logError('Unexpected login issue, session user null');
      showLoginError('Failed to login, please call Karius at (866) 452-7487 for account assistance');
      setLoading(false);
      return;
    }

    // if user role not found
    if (!hasValidUserRole(sessionUser)) {
      logError('No user role found', sessionUser);
      showLoginError('No user role found, please call Karius at (866) 452-7487 for account assistance');
      setLoading(false);
      return;
    }
    await identify(session);

    // On successful login, clear window.sessionStorage items
    // that are for a specific user since a new user has logged in
    clearUserSessionStorage();

    /* if user is redirected to login from some other page and has  returnToPath
                    query param set then redirect there after successfully login otherwise redirect
                    based on user role*/
    if (router?.query?.returnToPath) {
      let redirectPath: any = router?.query?.returnToPath;
      //leading slashes have been breaking things
      await router.push(redirectPath).catch((e) => {
        //if a custom redirect fails, log it, then them instead to the default for their role.
        logError('error pushing to this redirect path', { redirectPath, e });
        console.error('error pushing to this redirect path', {
          redirectPath,
          e,
        });
      });
      return;
    }
    await redirectByUserRole(sessionUser.role);
  };

  function hasValidUserRole(sessionUser: AppUser) {
    return sessionUser?.role;
  }

  return (
    <>
      <Grid container justifyContent='center'>
        <Grid
          item
          container
          maxWidth='1100px'
          sx={{
            padding: {
              md: '60px 40px 91px',
              sm: '0px',
            },
            '& .link-text': {
              color: Colors.teal,
              fontWeight: 500,
            },
          }}
        >
          <Grid
            item
            container
            sx={{
              justifyContent: {
                md: 'flex-start',
                xs: 'center',
              },
              gap: '30px',
              marginBottom: '21px',
            }}
            md={6}
            xs={12}
          >
            <Grid
              item
              container
              alignItems='center'
              flexDirection='column'
              sx={{
                backgroundColor: Colors.lightGray,
                boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.25)',
                maxWidth: {
                  md: '428px',
                },
              }}
            >
              <Grid
                item
                container
                justifyContent='center'
                sx={{
                  maxWidth: '328px',
                  padding: {
                    md: '35px 10px 38px',
                    xs: '54px 10px 53px',
                  },
                }}
              >
                <KariusLogoWidget />
                <Typography
                  variant='h4'
                  sx={{
                    margin: {
                      md: '16px 0px 49px',
                      xs: '16px 0px 46.43px',
                    },
                    color: Colors.black,
                    fontFamily: 'Arimo',
                    fontStyle: 'normal',
                    fontWeight: 'normal',
                    fontSize: '32px',
                    lineHeight: '37px',
                    textAlign: 'center',
                  }}
                >
                  Portal
                </Typography>
                <LoginForm onSubmit={onSubmitLogin} loading={loading} />
              </Grid>
            </Grid>
            <LoginHelpSection />
          </Grid>
          <Grid
            item
            container
            sx={{
              justifyContent: {
                md: 'flex-end',
                xs: 'center',
              },
              boxShadow: {
                md: 'none',
                xs: '0px 4px 5px rgba(0, 0, 0, 0.14), 0px 1px 10px rgba(0, 0, 0, 0.12), 0px 2px 4px rgba(0, 0, 0, 0.2)',
              },
              padding: {
                md: '0px',
                xs: '17px 24px',
              },
            }}
            md={6}
            xs={12}
          >
            <MobileMarketing />
          </Grid>
        </Grid>
      </Grid>
      <LoginFooter />
      <AccountBlocked
        open={accountBlockedModalOpen}
        handleClose={() => setAccountBlockedModalOpen(false)}
        isRedirectFromBlockedExpiredPage={isRedirectFromBlockedExpiredPage()}
      />
    </>
  );
}

const redirectByUserRole = async (roles: UserRole) => {
  let redirectUrl: string = getRoleBasedRedirectUrl(roles);
  logKariusEvent(EngagementEvents.login, { redirectURL: redirectUrl });
  await Router.push(redirectUrl).catch((e) => {
    //if a custom redirect fails, log it, then them instead to the default for their role.
    logError('error pushing to the users role redirect', {
      redirectUrl,
      e,
    });
    console.error('error pushing to the users role redirect', {
      redirectUrl,
      e,
    });
  });
};

export default Login;
